/**
 * Pre-order Countdown Timer
 * Updates countdown display every second until end date
 */
class PreorderCountdown {
  constructor(container) {
    this.container = container;
    // Support both start-timestamp and end-timestamp (Unix timestamps in seconds)
    // Timestamps are timezone-agnostic (always UTC), so this avoids timezone issues
    const timestamp = container.dataset.endTimestamp || container.dataset.startTimestamp;
    
    if (timestamp) {
      const timestampValue = parseInt(timestamp, 10);
      if (!isNaN(timestampValue) && timestampValue > 0) {
        // The timestamp from Shopify is in UTC (seconds since epoch)
        // It represents the UTC moment that corresponds to the JST time set in admin
        // Since shop is in JST, if user sets "2024-01-01 12:00", Shopify converts it to UTC
        // (12:00 JST = 03:00 UTC, so timestamp represents 03:00 UTC)
        this.targetTimestamp = timestampValue;
        // Create Date object from UTC timestamp
        this.targetDate = new Date(timestampValue * 1000);
        
        // Debug: Log the conversion for verification
        const rawDate = container.dataset.endDateRaw || container.dataset.startDateRaw;
        const jstDate = container.dataset.endJst || container.dataset.startJst;
        console.log('Preorder countdown initialized:', {
          rawDate: rawDate,
          jstDateFromLiquid: jstDate,
          timestamp: timestampValue,
          dateUTC: this.targetDate.toISOString(),
          dateLocal: this.targetDate.toString(),
          dateJST: new Date(timestampValue * 1000).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }),
          nowUTC: Math.floor(Date.now() / 1000),
          nowJST: new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }),
          diffHours: ((timestampValue - Math.floor(Date.now() / 1000)) / 3600).toFixed(2)
        });
      } else {
        this.targetDate = null;
        this.targetTimestamp = null;
      }
    } else {
      this.targetDate = null;
      this.targetTimestamp = null;
    }
    
    this.timerElement = container.querySelector('.preorder-countdown-timer');
    this.intervalId = null;
    this.hasReloaded = false;
    
    // Check if date is valid and in the future before initializing
    // Use UTC timestamps for accurate comparison
    if (this.timerElement && this.targetTimestamp) {
      const nowUTC = Math.floor(Date.now() / 1000); // Current time in UTC (seconds)
      const diff = this.targetTimestamp - nowUTC;
      
      // Only initialize countdown if date is in the future
      // If date is already expired, don't start countdown and don't reload
      if (diff > 0) {
        this.init();
      } else {
        // Date has already expired - don't start countdown, don't reload
        // The page state should already be correct (showing ended state)
        console.log('Countdown date has already expired, skipping countdown initialization');
      }
    }
  }
  
  init() {
    this.update();
    this.intervalId = setInterval(() => this.update(), 1000);
  }
  
  update() {
    // Use UTC timestamps for accurate comparison (timezone-agnostic)
    const nowUTC = Math.floor(Date.now() / 1000); // Current time in UTC (seconds)
    const diff = this.targetTimestamp - nowUTC; // Difference in seconds
    
    // Debug: Log timing information periodically (every minute)
    if (Math.floor(diff) % 60 === 0) {
      const targetDateJST = new Date(this.targetTimestamp * 1000).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
      const nowJST = new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
      console.log('Countdown status:', {
        targetJST: targetDateJST,
        nowJST: nowJST,
        diffSeconds: diff,
        diffHours: (diff / 3600).toFixed(2)
      });
    }
    
    // Only reload if countdown reaches zero (not if it was already past)
    if (diff <= 0) {
      this.complete();
      return;
    }
    
    // Calculate time components from seconds
    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    
    const daysEl = this.timerElement.querySelector('[data-days]');
    const hoursEl = this.timerElement.querySelector('[data-hours]');
    const minutesEl = this.timerElement.querySelector('[data-minutes]');
    const secondsEl = this.timerElement.querySelector('[data-seconds]');
    
    // For days: if 10 or less, don't pad with zero (remove leading zero)
    if (daysEl) {
      if (days <= 10) {
        daysEl.textContent = String(days);
      } else {
        daysEl.textContent = String(days).padStart(2, '0');
      }
    }
    if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
    if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
    if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
  }
  
  complete() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    
    // Prevent infinite reload loop
    // Check if we've already reloaded in this session
    const reloadKey = `preorder_reloaded_${this.targetDate.getTime()}`;
    if (sessionStorage.getItem(reloadKey)) {
      console.log('Already reloaded once for this countdown, preventing infinite loop');
      return;
    }
    
    // Mark that we're about to reload
    sessionStorage.setItem(reloadKey, 'true');
    this.hasReloaded = true;
    
    // Reload page only once when countdown actually reaches zero
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }
  
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
}

// Initialize all countdown timers on page load
document.addEventListener('DOMContentLoaded', () => {
  // Support both countdown modes: before start (data-start-timestamp) and until end (data-end-timestamp)
  const countdownContainers = document.querySelectorAll('.preorder-countdown[data-start-timestamp], .preorder-countdown[data-end-timestamp]');
  const countdowns = [];
  
  countdownContainers.forEach(container => {
    countdowns.push(new PreorderCountdown(container));
  });
  
  // Clean up on page unload
  window.addEventListener('beforeunload', () => {
    countdowns.forEach(countdown => countdown.destroy());
  });
});
